/**
 * Quiz widget (QL-13) — pure, shared by the advisor wizard and the public flow
 * renderer. The public renderer imports this file directly rather than keeping
 * a copy, so the two surfaces cannot drift.
 *
 * Shows one card per quiz the component points at: name, the client's standing,
 * and the button that opens the runner. Passing is permanent, so a passed quiz
 * collapses to a green line with the date rather than offering a retake.
 *
 * QL-505 — a group without a representative answers per member: the card
 * still shows the group's own (aggregate) standing, plus a count and,
 * expanded, one line per member with that member's own standing and action.
 *
 * Everything here is presentational. The parent owns starting an attempt,
 * grading it and overriding, because those differ per surface.
 */

import { memberLineAttribution, parseDbTimestamp } from "@repo/shared";
import type {
  AssessmentAttribution,
  QuizAttemptAvailability,
  QuizExternalResult,
  QuizForInstance,
  QuizOverride,
  QuizResultStatus,
  Translated,
} from "@repo/shared/types";
import { CheckCircle2, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { MemberLine } from "./product-assessment/member-line";
import { T } from "./quiz-widget.i18n";
import {
  RepresentativeNote,
  representativeNoteText,
  resolveAttributionBanner,
} from "./representative-note";

type QuizWidgetT = Translated<typeof T>;

export type QuizWidgetProps = {
  /** Null while loading; undefined entry = the component has no questions yet. */
  standing: QuizForInstance | null | undefined;
  /** QL-505 — `subjectId` is the member whose line was clicked; absent for every other client. */
  onStart: (subjectId?: string) => void;
  isStarting?: boolean;
  disabled?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
  /**
   * Advisor surface only. Absent on the public flow, which is what keeps a
   * client from clearing their own gate.
   */
  onOverride?: (subjectId?: string) => void;
  /** Who confirmed the external test, when the quiz was passed that way (top-level / single-subject standing only). */
  overrideByName?: string | null;
  /** QL-414 — the reason recorded with that confirmation. */
  overrideReason?: string | null;
  /** QL-505 — server-decided attribution banner. */
  attribution?: AssessmentAttribution;
  /** QL-505 — set only on a member-scoped client session: the one member line that session may act on. */
  sessionSubjectId?: string | null;
  /** QL-505 — a group session viewing a members-mode group: every member line is read-only. */
  readOnlyMembers?: boolean;
};

type StandingLine = {
  status: QuizResultStatus;
  availability: QuizAttemptAvailability;
  passedAt: string | null;
  override: (Pick<QuizOverride, "byUserName"> & Partial<Pick<QuizOverride, "reason">>) | null;
  externalResult: QuizExternalResult | null;
};

function renderAvailability(
  line: StandingLine,
  t: QuizWidgetT,
  formatRegionalDate: (d: Date | null) => string,
) {
  if (line.status === "passed") return null;
  if (line.availability.canAttempt) {
    if (line.availability.remaining == null) return null;
    return (
      <p className="text-sm text-muted-foreground">
        {line.availability.remaining === 1
          ? t.oneAttemptRemaining
          : t.attemptsRemaining(line.availability.remaining)}
      </p>
    );
  }
  if (line.availability.cooldownUntil) {
    return (
      <p className="text-sm text-muted-foreground">
        {t.retryAvailableOn(formatRegionalDate(parseDbTimestamp(line.availability.cooldownUntil)))}
      </p>
    );
  }
  return <p className="text-sm text-muted-foreground">{t.noAttemptsRemaining}</p>;
}

/**
 * One standing's status + availability + action — the same UI for the
 * single-subject card body and for one member's expanded line (QL-505).
 */
function QuizStandingBody({
  line,
  actionable,
  isStarting,
  onStart,
  onOverride,
  t,
  formatRegionalDate,
}: {
  line: StandingLine;
  actionable: boolean;
  isStarting: boolean;
  onStart: () => void;
  onOverride?: () => void;
  t: QuizWidgetT;
  formatRegionalDate: (d: Date | null) => string;
}) {
  const passed = line.status === "passed";
  const canAttempt = line.availability.canAttempt && actionable;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="space-y-1">
        {passed ? (
          <p className="flex items-center gap-2 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            {t.passed}
            {line.passedAt && ` · ${formatRegionalDate(parseDbTimestamp(line.passedAt))}`}
            {line.override?.byUserName &&
              ` · ${t.externalTestConfirmedBy(line.override.byUserName)}`}
          </p>
        ) : (
          line.status === "failed" && (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4" />
              {t.notPassed}
            </p>
          )
        )}
        {passed && line.override?.reason && (
          <p className="text-sm text-muted-foreground">{t.overrideReason(line.override.reason)}</p>
        )}
        {/* An attestation from another system: say so, and name it. The
            status line alone would read exactly like a pass earned here,
            and the advisor is the one who has to answer for the difference. */}
        {line.externalResult && (
          <p className="text-sm text-muted-foreground">
            {t.recordedExternally}
            {` · ${line.externalResult.source}`}
            {` · ${formatRegionalDate(parseDbTimestamp(line.externalResult.completedAt))}`}
          </p>
        )}
        {renderAvailability(line, t, formatRegionalDate)}
      </div>

      {!passed && actionable && (
        <div className="flex shrink-0 gap-2">
          {/* The way out of the dead end: a subject who failed out of their
              attempts has nothing left to press, so the advisor does. */}
          {onOverride && !line.availability.canAttempt && (
            <Button variant="outline" onClick={onOverride}>
              {t.override}
            </Button>
          )}
          <Button onClick={onStart} disabled={!canAttempt || isStarting}>
            {line.status === "failed" ? t.retryQuiz : t.startQuiz}
          </Button>
        </div>
      )}
    </div>
  );
}

export function QuizWidget({
  standing,
  onStart,
  isStarting = false,
  disabled = false,
  style = "none",
  title,
  description,
  language = "en_GB",
  hasValidationError = false,
  onOverride,
  overrideByName,
  overrideReason,
  attribution,
  sessionSubjectId = null,
  readOnlyMembers = false,
}: QuizWidgetProps) {
  const baseT = useT(T);
  // QL-654 — 2+ representatives answer in the same list as a group's members,
  // headed "Representatives" instead of "Members".
  const isRepresentativeList =
    attribution?.kind === "members" && attribution.basis === "representatives";
  const t = isRepresentativeList
    ? {
        ...baseT,
        expandMembers: baseT.expandRepresentatives,
        collapseMembers: baseT.collapseRepresentatives,
      }
    : baseT;
  const { formatDate: formatRegionalDate } = useRegionalFormat();
  const [membersExpanded, setMembersExpanded] = useState(false);

  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : style === "border"
      ? "border rounded-lg p-4"
      : style === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  if (!standing) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noQuiz}
      </div>
    );
  }

  const { availability, status, passedAt, externalResult, members } = standing;
  const isMembersMode = !!members && members.length > 0;
  const banner = resolveAttributionBanner(attribution, undefined);
  const isMemberLink = sessionSubjectId != null;

  const isMemberActionable = (subjectId: string) => {
    if (disabled || readOnlyMembers) return false;
    if (sessionSubjectId != null) return sessionSubjectId === subjectId;
    return true;
  };

  const passedCount = members?.filter((m) => m.status === "passed").length ?? 0;
  const countText = t.memberCountPassed(passedCount, members?.length ?? 0);

  // Spec UX — on a member link, the row auto-expands to that member's own
  // line, and only that line shows; the aggregate count stays computed from
  // the full group above.
  const membersRowExpanded = isMemberLink || membersExpanded;
  const visibleMembers = isMemberLink
    ? members?.filter((m) => m.subjectId === sessionSubjectId)
    : members;

  return (
    <div className={wrapperClass} data-testid="quiz-widget">
      <div className="space-y-3">
        {/* QL-13 — the component's title and description ARE the quiz's name and
            description, so show them once. `standing.quiz` is the fallback for
            a component whose title the designer left blank. */}
        <FlowComponentHeader
          title={title || standing.quiz.name}
          description={description || standing.quiz.description}
        />
        {hasValidationError && status !== "passed" && (
          <p className="text-sm text-destructive">{t.incompleteMessage}</p>
        )}
        {banner && (
          <RepresentativeNote
            text={representativeNoteText(
              t.repNote,
              banner,
              t.repGeneric,
              language,
              t.repNoteViaMember,
            )}
          />
        )}
        {isRepresentativeList && <RepresentativeNote text={t.repListNote} />}

        {isMembersMode ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setMembersExpanded((v) => !v)}
              aria-expanded={membersRowExpanded}
              aria-label={membersRowExpanded ? t.collapseMembers : t.expandMembers}
              className="flex min-h-9 w-full items-center justify-between gap-3 text-left"
            >
              <span className="flex items-center gap-2">
                {membersRowExpanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                {status === "passed" ? (
                  <span className="flex items-center gap-2 text-sm text-success">
                    <CheckCircle2 className="h-4 w-4" />
                    {t.passed}
                    {passedAt && ` · ${formatRegionalDate(parseDbTimestamp(passedAt))}`}
                  </span>
                ) : status === "unanswered" ? (
                  <span className="text-sm text-muted-foreground">{t.notStarted}</span>
                ) : (
                  <span className="text-sm text-muted-foreground">{t.notPassed}</span>
                )}
              </span>
              <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground">
                {countText}
              </span>
            </button>
            {membersRowExpanded && (
              <div className="space-y-1 pl-6">
                {visibleMembers?.map((member) => (
                  <MemberLine
                    key={member.subjectId}
                    subjectId={member.subjectId}
                    name={member.name}
                    {...memberLineAttribution({
                      subjectId: member.subjectId,
                      liveRepresentative: member.memberRepresentative,
                      answered: member.status !== "unanswered",
                      answeredBy: member.answeredBy,
                      advisorOrExternal: !!member.override || !!member.externalResult,
                    })}
                    language={language}
                  >
                    <QuizStandingBody
                      line={{
                        status: member.status,
                        availability: member.availability,
                        passedAt: member.passedAt,
                        override: member.override,
                        externalResult: member.externalResult,
                      }}
                      actionable={isMemberActionable(member.subjectId)}
                      isStarting={isStarting}
                      onStart={() => onStart(member.subjectId)}
                      onOverride={
                        onOverride && !disabled ? () => onOverride(member.subjectId) : undefined
                      }
                      t={t}
                      formatRegionalDate={formatRegionalDate}
                    />
                  </MemberLine>
                ))}
              </div>
            )}
          </div>
        ) : (
          <QuizStandingBody
            line={{
              status,
              availability,
              passedAt,
              override: overrideByName
                ? { byUserName: overrideByName, reason: overrideReason ?? undefined }
                : null,
              externalResult,
            }}
            actionable={!disabled}
            isStarting={isStarting}
            onStart={() => onStart()}
            onOverride={onOverride && !disabled ? () => onOverride() : undefined}
            t={t}
            formatRegionalDate={formatRegionalDate}
          />
        )}
      </div>
    </div>
  );
}
